/**
 * Intestines organ texture — anatomical view in BrainPOP "Guts and Bolts" style.
 *
 *   • LARGE INTESTINE (colon): thick olive/green tube. Starts bottom-left
 *     (cecum / ileocecal valve), runs up the LEFT side (ascending colon),
 *     across the TOP (transverse colon), down the RIGHT side (descending
 *     colon), then turns LEFT and runs along the bottom (sigmoid colon),
 *     terminating above the bottom waste cone at col 2 (rectum exit).
 *     Surface has haustral bands.
 *
 *   • SMALL INTESTINE: tangled pink coiled tube filling the interior space
 *     inside the U.
 *
 * 4 cells wide × 3 cells tall = 200 × 150 px.
 */
import Phaser from 'phaser';
import { GRID_SIZE } from '../../grid';
import type { HasTexture } from './core';

export function registerIntestinesTextures(scene: Phaser.Scene, has: HasTexture): void {
  if (!has('organ-intestines-idle')) makeIntestines(scene, 'organ-intestines-idle', false);
  if (!has('organ-intestines-active')) makeIntestines(scene, 'organ-intestines-active', true);
}

function makeIntestines(scene: Phaser.Scene, key: string, active: boolean): void {
  const cols = 4;
  const rows = 3;
  const w = cols * GRID_SIZE; // 200
  const h = rows * GRID_SIZE; // 150
  const g = scene.add.graphics();

  // ---------- Color palette ----------
  // Active variant brightens/warms both tubes (digestion "lit up") and adds a
  // soft activation glow + a couple of status pips — mirrors the idle→active
  // treatment the other organs use (e.g. stomach).
  // Colon (large intestine) — olive / yellow-green like the reference art
  const colonFill = active ? 0xaec556 : 0x8a9c3e;
  const colonHi = active ? 0xd6e88a : 0xb4c763;
  const colonShade = active ? 0x74822e : 0x5a6520;
  const colonOutline = 0x2d3410;
  // Small intestine — bubblegum pink with darker outline
  const siFill = active ? 0xffc2d2 : 0xf2adbe;
  const siHi = active ? 0xffe3ec : 0xfbd0db;
  const siShade = active ? 0xd98298 : 0xc66f86;
  const siOutline = 0x6b2c3c;

  // ---------- Colon path waypoints ----------
  // Starts at the bottom-left (cecum), runs up the left, across the top, down
  // the right, then leftward along the bottom to terminate above the bottom
  // waste cone (which is at col 2 — center of the organ — per the level
  // configs). The tube is then drawn by stamping overlapping circles along
  // this path, which gives clean rounded corners automatically.
  const margin = 22;
  const tubeR = 17; // colon outer radius
  // Bottom waste cone is at cellOffset { dCol: 2, dRow: 3 } — col 2 center is
  // at x = 2 * GRID_SIZE + GRID_SIZE/2 = 125 in organ-local coords.
  const exitX = 2 * GRID_SIZE + GRID_SIZE / 2;
  const waypoints: [number, number][] = [
    [margin, h - margin + 4], // bottom-left (cecum) — sticks down slightly
    [margin, margin], // up the left side
    [w - margin, margin], // across the top
    [w - margin, h - margin + 4], // down the right side
    [exitX, h - margin + 4], // across the bottom to the rectum exit (col 2)
  ];

  // Densify into a single point list
  const pathPoints: [number, number][] = [];
  for (let i = 0; i < waypoints.length - 1; i++) {
    const [x0, y0] = waypoints[i];
    const [x1, y1] = waypoints[i + 1];
    const dist = Math.hypot(x1 - x0, y1 - y0);
    const steps = Math.max(2, Math.ceil(dist / 2));
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      pathPoints.push([x0 + (x1 - x0) * t, y0 + (y1 - y0) * t]);
    }
  }

  // ---------- Colon: drop shadow ----------
  g.fillStyle(0x000000, 0.22);
  for (const [px, py] of pathPoints) g.fillCircle(px + 1.5, py + 3, tubeR + 1.5);

  // ---------- Colon: dark outline (stamp slightly-larger black circles) ----------
  g.fillStyle(colonOutline, 1);
  for (const [px, py] of pathPoints) g.fillCircle(px, py, tubeR + 1.5);

  // ---------- Colon: main tube fill ----------
  g.fillStyle(colonFill, 1);
  for (const [px, py] of pathPoints) g.fillCircle(px, py, tubeR);

  // ---------- Colon: shaded under-side (gives the tube 3D form) ----------
  // Shade is offset DOWN and to the right (south-east), like a sun coming from
  // top-left.
  g.fillStyle(colonShade, 0.4);
  for (const [px, py] of pathPoints) g.fillCircle(px + 2, py + 3, tubeR * 0.78);

  // ---------- Colon: highlight strip along the top/outer edge ----------
  g.fillStyle(colonHi, 0.55);
  for (const [px, py] of pathPoints) g.fillCircle(px - 2, py - 3, tubeR * 0.55);

  // ---------- Colon: haustral bands (the segmented bulges of the colon) ----------
  // A short dark line perpendicular to the tube direction, at regular spacing.
  const haustraStep = 11;
  g.lineStyle(1.6, colonOutline, 0.6);
  for (let i = haustraStep; i < pathPoints.length - haustraStep; i += haustraStep) {
    const [px, py] = pathPoints[i];
    const [px2, py2] = pathPoints[Math.min(i + 1, pathPoints.length - 1)];
    const dx = px2 - px;
    const dy = py2 - py;
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    g.beginPath();
    g.moveTo(px + nx * (tubeR - 1), py + ny * (tubeR - 1));
    g.lineTo(px - nx * (tubeR - 1), py - ny * (tubeR - 1));
    g.strokePath();
  }

  // ---------- Small intestine: a long, densely coiled tube inside the U ----------
  // Hand-designed snake-path waypoints that wind back-and-forth through the
  // interior, with extra loops spilling into the open bottom-left area
  // (where the colon's sigmoid segment doesn't cover). Then we stamp
  // overlapping pink circles along the densified path.
  // Interior bounds (clear of the colon tube):
  const innerL = margin + tubeR - 2; // 37
  const innerR = w - margin - tubeR + 2; // 163
  const innerT = margin + tubeR - 2; // 37
  const innerB = h - margin - tubeR + 2; // 113 — top of the colon's bottom segment
  // Open bottom-left area (no colon there — between the left vertical and the
  // sigmoid segment): x in [margin, exitX - tubeR], y in [innerB, h - 4].
  const openBL_B = h - 6; // 144
  const innerCx = (innerL + innerR) / 2;
  const innerCy = (innerT + innerB) / 2;

  // Three big sweeping loops + a center cross-loop, plus a spill into the
  // bottom-left open area. Drawn as quadratic Bezier curves (densified into
  // stamped circles below) so each loop reads as a fat sausage rather than
  // a scribbly polyline.
  const siR = 11; // small-intestine tube radius — sausage-thick
  // Many overlapping loops spread across the ENTIRE interior so the pink
  // fills the colon densely (like the BrainPOP reference).
  const loops: [number, number, number, number, number, number][] = [
    // [x0, y0, ctrlX, ctrlY, x1, y1]
    // ===== UPPER HALF =====
    // Top arc 1: high sweep across the top
    [innerL + 6, innerT + 14, innerCx, innerT + 2, innerR - 8, innerT + 14],
    // Top arc 2: slightly below
    [innerR - 6, innerT + 8, innerCx + 8, innerT + 22, innerL + 8, innerT + 12],
    // Upper-left lobe
    [innerL + 4, innerT + 6, innerL + 6, innerT + 22, innerL + 30, innerT + 30],
    // Upper-right lobe
    [innerR - 4, innerT + 8, innerR - 6, innerT + 24, innerR - 28, innerT + 28],
    // Top-mid bulge dipping into mid
    [innerL + 28, innerT + 8, innerCx, innerT + 18, innerR - 26, innerT + 6],
    // ===== MIDDLE BAND =====
    // Diagonal cross A (upper-left to lower-right)
    [innerL + 8, innerCy - 10, innerCx, innerCy - 2, innerR - 10, innerCy + 8],
    // Diagonal cross B (upper-right to lower-left)
    [innerR - 8, innerCy - 12, innerCx + 4, innerCy + 4, innerL + 14, innerCy + 14],
    // Mid-left vertical squiggle
    [innerL + 6, innerCy - 4, innerL + 16, innerCy + 4, innerL + 6, innerCy + 14],
    // Mid-right vertical squiggle
    [innerR - 6, innerCy - 6, innerR - 18, innerCy + 2, innerR - 6, innerCy + 16],
    // Center figure-eight
    [innerCx - 16, innerCy - 8, innerCx + 14, innerCy + 2, innerCx + 18, innerCy + 12],
    [innerCx + 18, innerCy - 10, innerCx - 8, innerCy + 4, innerCx - 14, innerCy + 14],
    // ===== LOWER HALF =====
    // Bottom arc 1
    [innerL + 8, innerB - 8, innerCx, innerB + 4, innerR - 10, innerB - 8],
    // Bottom arc 2
    [innerR - 6, innerB - 14, innerCx + 4, innerB - 2, innerL + 10, innerB - 12],
    // Bottom-right lobe (terminus into sigmoid area)
    [innerCx + 12, innerB - 4, innerR - 26, innerB + 2, innerR - 10, innerCy + 12],
    // ===== BOTTOM-LEFT SPILL (open area, toward the blood cones) =====
    [innerL + 6, innerB - 4, innerL - 8, innerB + 8, innerL + 4, openBL_B - 6],
    [innerL - 8, openBL_B - 4, innerL + 16, openBL_B + 2, innerL + 40, openBL_B - 6],
    [innerL + 8, openBL_B - 14, innerL + 28, openBL_B - 4, innerL + 14, innerB - 2],
  ];

  const siPath: [number, number][] = [];
  for (const [x0, y0, cxp, cyp, x1, y1] of loops) {
    const steps = 26;
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const u = 1 - t;
      const x = u * u * x0 + 2 * u * t * cxp + t * t * x1;
      const y = u * u * y0 + 2 * u * t * cyp + t * t * y1;
      siPath.push([x, y]);
    }
  }

  // Drop shadow
  g.fillStyle(0x000000, 0.22);
  for (const [px, py] of siPath) g.fillCircle(px + 1, py + 2, siR + 1);
  // Dark outline
  g.fillStyle(siOutline, 1);
  for (const [px, py] of siPath) g.fillCircle(px, py, siR + 1);
  // Main pink fill
  g.fillStyle(siFill, 1);
  for (const [px, py] of siPath) g.fillCircle(px, py, siR);
  // Shading on bottom-right
  g.fillStyle(siShade, 0.4);
  for (const [px, py] of siPath) g.fillCircle(px + 1.5, py + 2, siR * 0.65);
  // Highlight on top-left
  g.fillStyle(siHi, 0.55);
  for (const [px, py] of siPath) g.fillCircle(px - 1.5, py - 2, siR * 0.45);

  // ---------- Activation glow (when active) ----------
  // A faint warm wash along the small-intestine coils + a couple of bright
  // pips, so the "digesting" state reads as the tract lighting up. Kept
  // subtle and on-theme (warm peach), echoing the stomach's active treatment.
  if (active) {
    g.fillStyle(0xffd28a, 0.18);
    for (const [px, py] of siPath) g.fillCircle(px - 1.5, py - 2, siR * 0.7);
    // Two small "active" status pips on the colon's upper edge.
    g.fillStyle(0xfff0b0, 1);
    g.fillCircle(margin + 4, margin - 2, 3);
    g.fillCircle(w - margin - 4, margin - 2, 3);
    g.fillStyle(0xffffff, 0.9);
    g.fillCircle(margin + 4 - 0.8, margin - 2 - 0.8, 1.1);
    g.fillCircle(w - margin - 4 - 0.8, margin - 2 - 0.8, 1.1);
  }

  g.generateTexture(key, w, h);
  g.destroy();
}
